import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './Widget.css';

interface GridLevel {
  price: number;
  quantity: number;
  side: 'buy' | 'sell';
  status: 'pending' | 'filled' | 'cancelled';
}

interface AsymmetricGridData {
  enabled: boolean;
  basePrice: number;
  currentPrice: number;
  gridLevels: GridLevel[];
  buyToSellRatio: number;
  totalBuyQty: number;
  totalSellQty: number;
  actualRatio: number;
  minCapitalToActivate: number;
  currentCapital: number;
}

export const AsymmetricGridVisualization: React.FC = () => {
  const [gridData, setGridData] = useState<AsymmetricGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // This would come from a grid-trading-status endpoint
        // For now, we'll use mock data structure that would be returned by the API
        const response = await api.tradingEngine.getStatus();
        // In production, extract grid data from response
        // setGridData(response.gridData);

        // Placeholder - in real implementation, this comes from API
        setGridData(null); // Will show "not activated" message
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch grid data');
        console.error('Grid visualization fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatBWS = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="widget asymmetric-grid">
        <h3>Asymmetric Grid Trading</h3>
        <div className="widget-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget asymmetric-grid">
        <h3>Asymmetric Grid Trading</h3>
        <div className="widget-error">{error}</div>
      </div>
    );
  }

  // Grid not activated yet (Month 1 - capital below $500)
  if (!gridData || !gridData.enabled) {
    return (
      <div className="widget asymmetric-grid">
        <div className="widget-header">
          <h3>Asymmetric Grid Trading</h3>
          <div className="status-badge disabled">STANDBY</div>
        </div>

        <div className="activation-pending">
          <div className="pending-icon">⏳</div>
          <h4>Awaiting Capital Threshold</h4>
          <p className="threshold-info">
            Grid trading will activate when capital reaches{' '}
            <strong>$500</strong>
          </p>
          {gridData && (
            <div className="progress-info">
              <div className="progress-label">
                Current Capital: {formatUSD(gridData.currentCapital)}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.min(
                      (gridData.currentCapital / gridData.minCapitalToActivate) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
              <div className="progress-percent">
                {((gridData.currentCapital / gridData.minCapitalToActivate) * 100).toFixed(1)}%
              </div>
            </div>
          )}
          <div className="strategy-preview">
            <h5>When Activated:</h5>
            <ul>
              <li>2x Buy Bias (2:1 buy/sell ratio)</li>
              <li>Buy Orders: 100,000 BWS per level</li>
              <li>Sell Orders: 50,000 BWS per level</li>
              <li>$100/day capital limit (Month 2)</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Grid is active - show visualization
  const buyLevels = gridData.gridLevels.filter((l) => l.side === 'buy');
  const sellLevels = gridData.gridLevels.filter((l) => l.side === 'sell');

  const priceRange = {
    min: Math.min(...gridData.gridLevels.map((l) => l.price)),
    max: Math.max(...gridData.gridLevels.map((l) => l.price)),
  };

  const getPricePosition = (price: number): number => {
    const range = priceRange.max - priceRange.min;
    return ((price - priceRange.min) / range) * 100;
  };

  return (
    <div className="widget asymmetric-grid">
      <div className="widget-header">
        <h3>Asymmetric Grid Trading</h3>
        <div className="status-badge enabled">ACTIVE</div>
      </div>

      <div className="grid-overview">
        <div className="overview-item">
          <label>Base Price</label>
          <div className="value">{formatUSD(gridData.basePrice)}</div>
        </div>
        <div className="overview-item">
          <label>Current Price</label>
          <div className="value current">{formatUSD(gridData.currentPrice)}</div>
        </div>
        <div className="overview-item">
          <label>Buy/Sell Ratio</label>
          <div className="value ratio">
            {gridData.actualRatio.toFixed(2)}:1
          </div>
        </div>
      </div>

      <div className="grid-visualization">
        <div className="price-scale">
          {/* Sell levels (above base) */}
          <div className="sell-levels">
            {sellLevels
              .sort((a, b) => b.price - a.price)
              .map((level, index) => (
                <div
                  key={`sell-${index}`}
                  className={`grid-level sell ${level.status}`}
                  style={{ bottom: `${getPricePosition(level.price)}%` }}
                >
                  <div className="level-info">
                    <span className="price">{formatUSD(level.price)}</span>
                    <span className="quantity">{formatBWS(level.quantity)} BWS</span>
                  </div>
                  <div className="level-bar sell-bar" />
                </div>
              ))}
          </div>

          {/* Current price indicator */}
          <div
            className="current-price-line"
            style={{ bottom: `${getPricePosition(gridData.currentPrice)}%` }}
          >
            <span className="price-label">Current: {formatUSD(gridData.currentPrice)}</span>
          </div>

          {/* Base price indicator */}
          <div
            className="base-price-line"
            style={{ bottom: `${getPricePosition(gridData.basePrice)}%` }}
          >
            <span className="price-label">Base: {formatUSD(gridData.basePrice)}</span>
          </div>

          {/* Buy levels (below base) */}
          <div className="buy-levels">
            {buyLevels
              .sort((a, b) => b.price - a.price)
              .map((level, index) => (
                <div
                  key={`buy-${index}`}
                  className={`grid-level buy ${level.status}`}
                  style={{ bottom: `${getPricePosition(level.price)}%` }}
                >
                  <div className="level-bar buy-bar" />
                  <div className="level-info">
                    <span className="price">{formatUSD(level.price)}</span>
                    <span className="quantity">{formatBWS(level.quantity)} BWS</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="grid-stats">
        <div className="stat-group">
          <h5>Buy Orders</h5>
          <div className="stat-value buy">{formatBWS(gridData.totalBuyQty)} BWS</div>
          <div className="stat-subvalue">{buyLevels.length} levels</div>
        </div>
        <div className="stat-group">
          <h5>Sell Orders</h5>
          <div className="stat-value sell">{formatBWS(gridData.totalSellQty)} BWS</div>
          <div className="stat-subvalue">{sellLevels.length} levels</div>
        </div>
      </div>

      <div className="grid-info">
        <div className="info-badge">
          <span className="info-icon">📈</span>
          <span className="info-text">
            2x buy bias accumulates BWS while providing sell-side liquidity
          </span>
        </div>
      </div>
    </div>
  );
};
